import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { PencilSquareIcon } from '@heroicons/react/24/outline'
import { getDistributionHistory, generateWaterfallJournalEntry, updateDistributionNote } from '@/services/waterfall'
import type { DistributionResponse, MemberAmounts, TierBreakdown } from '@/types/waterfall'
import { MEMBERS, TIER_LABELS } from '@/types/waterfall'
import { useMaxMd } from '@/hooks/useMaxMd'
import { usePageAccess } from '@/hooks/usePageAccess'
import { formatApiError } from '@/utils/apiError'
import { toast } from '@/store/toastStore'
import WaterfallTabHeader from './WaterfallTabHeader'
import AllocationHistory from './AllocationHistory'
import DistributionModeToggle from './DistributionModeToggle'
import WaterfallJournalEntryCard from './WaterfallJournalEntryCard'
import DistributionJeRowButton from './DistributionJeRowButton'
import {
  JE_COL_CLASS,
  MOBILE_NOTE_TOGGLE_COL_CLASS,
  MOBILE_NUM_COL_CLASS,
  NOTE_COL_CLASS,
  NUM_COL_CLASS,
  STICKY_DATE_CHILD_CLASS,
  STICKY_DATE_CHILD_MOBILE_CLASS,
  STICKY_DATE_CLASS,
  STICKY_DATE_EXPANDED_CLASS,
  STICKY_DATE_HEAD_CLASS,
  STICKY_DATE_HEAD_MOBILE_CLASS,
  STICKY_DATE_MOBILE_CLASS,
  STICKY_DATE_TOTALS_CLASS,
  STICKY_DATE_TOTALS_MOBILE_CLASS,
  STICKY_HEAD_CLASS,
  STICKY_NUM_CHILD_CLASS,
  STICKY_NUM_CLASS,
  STICKY_NUM_HEAD_CLASS,
  STICKY_NUM_TOTALS_CLASS,
  STICKY_TOTALS_CLASS,
  TOTAL_COL_BORDER,
} from './distributionHistoryTableLayout'
import { parseJeGenerateError } from './allocationMonthReadinessUtils'
import { useDistributionModeStore } from '@/store/distributionModeStore'
import { HoverHelp } from '@/tableControls'

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

const TIER_ORDER: (keyof TierBreakdown)[] = ['t1', 'hp', 's3a', 's3b', 's4a', 's4b']
const VERIFIED_STORAGE_KEY = 'waterfall-history-verified-v1'

function readVerifiedCells(): Set<string> {
  try {
    const raw = localStorage.getItem(VERIFIED_STORAGE_KEY)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((key): key is string => typeof key === 'string'))
  } catch {
    return new Set()
  }
}

function writeVerifiedCells(cells: Set<string>): void {
  try {
    localStorage.setItem(VERIFIED_STORAGE_KEY, JSON.stringify([...cells]))
  } catch {
    // Ignore quota, privacy mode, or SSR environments without storage.
  }
}

function verifiedCellKey(
  distributionId: number,
  tierKey: keyof TierBreakdown,
  memberOrTotal: string,
): string {
  return `${distributionId}:${tierKey}:${memberOrTotal}`
}

function tierTotal(tier: MemberAmounts): number {
  return MEMBERS.reduce((sum, m) => sum + Number(tier[m]), 0)
}

function nonEmptyTiers(row: DistributionResponse): (keyof TierBreakdown)[] {
  return TIER_ORDER.filter((tierKey) => tierTotal(row[tierKey]) !== 0)
}

function memberWithheldTotal(row: DistributionResponse, member: (typeof MEMBERS)[number]): number {
  const w = row.withheld
  if (!w) return 0
  return Number(w.s3b[member]) + Number(w.s4b[member])
}

function rowHasCatchUp(row: DistributionResponse): boolean {
  return MEMBERS.some((m) => Number(row.catch_up_by_member?.[m] ?? 0) > 0.005)
}

function NoteCell({
  row,
  onSaved,
}: {
  row: DistributionResponse
  onSaved: (updated: DistributionResponse) => void
}) {
  const [value, setValue] = useState(row.note ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setValue(row.note ?? '')
  }, [row.note])

  const save = async () => {
    const trimmed = value.trim()
    const nextNote = trimmed.length > 0 ? trimmed : null
    if (nextNote === (row.note ?? null)) return

    setSaving(true)
    setError(null)
    try {
      const updated = await updateDistributionNote(row.id, nextNote)
      onSaved(updated)
    } catch (e) {
      setError(formatApiError(e, 'Failed to save note'))
      setValue(row.note ?? '')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex items-start gap-1">
      <input
        type="text"
        value={value}
        disabled={saving}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur()
          }
        }}
        className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-gray-700 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-gray-50"
        placeholder="Add a note…"
      />
      {row.warnings.length > 0 && (
        <span className="shrink-0 text-amber-600 pt-1" title={row.warnings.join('; ')}>
          ⚠
        </span>
      )}
      {error && (
        <span className="sr-only" role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

function VerifiableTierAmountCell({
  cellKey,
  amount,
  isVerified,
  onToggle,
  colClass = NUM_COL_CLASS,
  touchFriendly = false,
}: {
  cellKey: string
  amount: number
  isVerified: boolean
  onToggle: (key: string) => void
  colClass?: string
  touchFriendly?: boolean
}) {
  return (
    <td
      role="button"
      tabIndex={0}
      aria-pressed={isVerified}
      title={isVerified ? 'Mark as unchecked' : 'Mark as checked'}
      onClick={() => onToggle(cellKey)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggle(cellKey)
        }
      }}
      className={`${colClass} cursor-pointer text-right tabular-nums select-none ${
        touchFriendly ? 'min-h-11 px-2 py-2' : 'px-4 py-2'
      } ${isVerified ? 'bg-emerald-50' : ''} ${amount < 0 ? 'text-red-600' : ''}`}
    >
      {isVerified && <span className="text-emerald-600/70">✓ </span>}
      {fmt.format(amount)}
    </td>
  )
}

function HistoryCash() {
  const maxMd = useMaxMd()
  const { canEdit } = usePageAccess('distributions')
  const numColClass = maxMd ? MOBILE_NUM_COL_CLASS : NUM_COL_CLASS
  const noteColClass = maxMd ? MOBILE_NOTE_TOGGLE_COL_CLASS : NOTE_COL_CLASS
  const cellPadClass = maxMd ? 'px-2 py-2' : 'px-4 py-2'
  const stickyDateClass = maxMd ? STICKY_DATE_MOBILE_CLASS : STICKY_DATE_CLASS
  const stickyDateHeadClass = maxMd ? STICKY_DATE_HEAD_MOBILE_CLASS : STICKY_DATE_HEAD_CLASS
  const stickyDateTotalsClass = maxMd ? STICKY_DATE_TOTALS_MOBILE_CLASS : STICKY_DATE_TOTALS_CLASS
  const stickyDateChildClass = maxMd ? STICKY_DATE_CHILD_MOBILE_CLASS : STICKY_DATE_CHILD_CLASS
  const tableColCount = MEMBERS.length + 6
  const [rows, setRows] = useState<DistributionResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [noteExpanded, setNoteExpanded] = useState<Set<string>>(new Set())
  const [verifiedCells, setVerifiedCells] = useState<Set<string>>(() => readVerifiedCells())
  const [activeJeDistributionNum, setActiveJeDistributionNum] = useState<number | null>(null)
  const [jeLoadingNum, setJeLoadingNum] = useState<number | null>(null)
  const refreshRows = useCallback(async () => {
    try {
      const data = await getDistributionHistory()
      setRows([...data.distributions].sort((a, b) => b.num - a.num))
      setError(null)
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to refresh history'))
    }
  }, [])

  const loadHistory = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getDistributionHistory()
      setRows([...data.distributions].sort((a, b) => b.num - a.num))
    } catch (e) {
      setError(formatApiError(e, 'Failed to load history'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadHistory()
  }, [loadHistory])

  const handleJeRowClick = useCallback(
    async (row: DistributionResponse) => {
      const jeStatus = row.je_status ?? 'none'

      if (jeStatus === 'posted') {
        if (row.je_qbo_url) {
          window.open(row.je_qbo_url, '_blank', 'noopener,noreferrer')
        } else {
          toast.error('QuickBooks link is not available for this journal entry')
        }
        return
      }

      if (jeStatus === 'draft' || jeStatus === 'stale') {
        setActiveJeDistributionNum(row.num)
        return
      }

      if (!canEdit) return

      setJeLoadingNum(row.num)
      try {
        await generateWaterfallJournalEntry({
          mode: 'cash',
          distribution_num: row.num,
        })
        setActiveJeDistributionNum(row.num)
        await refreshRows()
        toast.success('Journal entry draft created')
      } catch (e) {
        const parsed = parseJeGenerateError(e)
        toast.error(parsed.message)
      } finally {
        setJeLoadingNum(null)
      }
    },
    [canEdit, refreshRows],
  )
  const handleNoteSaved = useCallback((updated: DistributionResponse) => {
    setRows((prev) => prev.map((row) => (row.id === updated.id ? updated : row)))
  }, [])

  const cumulativeTotals = useMemo(() => {
    const byMember = MEMBERS.reduce((acc, m) => {
      acc[m] = 0
      return acc
    }, {} as MemberAmounts)
    let total = 0
    let cashTotal = 0
    for (const row of rows) {
      for (const m of MEMBERS) {
        // by_member values arrive as decimal strings, so coerce before summing
        // to avoid string concatenation (which yields NaN once formatted).
        byMember[m] += Number(row.by_member[m])
      }
      total += Number(row.total)
      cashTotal += Number(row.cash_total ?? row.total)
    }
    return { byMember, total, cashTotal }
  }, [rows])

  const expandableKeys = useMemo(
    () => rows.filter((row) => nonEmptyTiers(row).length > 0).map((row) => String(row.id)),
    [rows],
  )

  const allExpanded =
    expandableKeys.length > 0 && expandableKeys.every((key) => expanded.has(key))

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleNoteExpanded = (key: string) => {
    setNoteExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleAll = () => {
    if (allExpanded) {
      setExpanded(new Set())
      return
    }
    setExpanded(new Set(expandableKeys))
  }

  const toggleVerified = useCallback((key: string) => {
    setVerifiedCells((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      writeVerifiedCells(next)
      return next
    })
  }, [])

  const clearVerified = useCallback(() => {
    const next = new Set<string>()
    writeVerifiedCells(next)
    setVerifiedCells(next)
  }, [])

  if (loading) {
    return <p className="text-sm text-gray-500">Loading distribution history…</p>
  }

  if (error) {
    return (
      <div className="space-y-3">
        <WaterfallTabHeader
          title="History"
          description="All distributions logged in BookKeeper — the system of record for actual cash paid."
        />
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
        <button
          type="button"
          onClick={() => void loadHistory()}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="shrink-0">
        <WaterfallTabHeader
          title="History"
          description="All distributions logged in BookKeeper — the system of record for actual cash paid."
        />
      </div>

      {activeJeDistributionNum != null ? (
        <div className="mb-4 shrink-0">
          <WaterfallJournalEntryCard
            mode="cash"
            distributionNum={activeJeDistributionNum}
            distributionLabel={`Distribution #${activeJeDistributionNum}`}
            onDismiss={() => setActiveJeDistributionNum(null)}
            onChanged={() => void refreshRows()}
          />
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
          No distributions logged yet. Use the Calculator to propose and log a distribution.
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 py-2">
            <p className="text-sm text-gray-500">
              {rows.length} distribution{rows.length === 1 ? '' : 's'} logged
            </p>
            {(verifiedCells.size > 0 || expandableKeys.length > 0) && (
              <div className="flex items-center gap-2">
                {verifiedCells.size > 0 && (
                  <button
                    type="button"
                    onClick={clearVerified}
                    className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 max-md:min-h-11 max-md:px-4 max-md:text-sm"
                  >
                    Clear indicators
                  </button>
                )}
                {expandableKeys.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 max-md:min-h-11 max-md:px-4 max-md:text-sm"
                  >
                    {allExpanded ? 'Collapse all' : 'Expand all'}
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="table-scroll-mobile min-h-0 flex-1 overflow-x-auto overflow-y-auto overscroll-x-contain">
            <table className="min-w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className={`${STICKY_NUM_HEAD_CLASS} ${cellPadClass} text-left font-medium text-gray-600`}>
                    #
                  </th>
                  <th className={`${stickyDateHeadClass} ${cellPadClass} text-left font-medium text-gray-600`}>
                    Date
                  </th>
                  <th
                    className={`${JE_COL_CLASS} ${STICKY_HEAD_CLASS} ${cellPadClass} text-left font-medium text-gray-600`}
                  >
                    JE
                  </th>
                  {MEMBERS.map((m) => (
                    <th
                      key={m}
                      className={`${numColClass} ${STICKY_HEAD_CLASS} ${cellPadClass} text-right font-medium text-gray-600`}
                    >
                      {m}
                    </th>
                  ))}
                  <th
                    className={`${numColClass} ${STICKY_HEAD_CLASS} ${cellPadClass} text-right font-medium text-gray-600 ${TOTAL_COL_BORDER}`}
                  >
                    Total
                  </th>
                  <th
                    className={`${numColClass} ${STICKY_HEAD_CLASS} ${cellPadClass} text-right font-medium text-gray-600`}
                  >
                    Cash paid
                  </th>
                  <th
                    className={`${STICKY_HEAD_CLASS} ${noteColClass} ${cellPadClass} text-left font-medium text-gray-600`}
                  >
                    {maxMd ? <span className="sr-only">Note</span> : 'Note'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr aria-label="Cumulative totals paid to date (locked)">
                  <td className={`${STICKY_NUM_TOTALS_CLASS} ${cellPadClass} font-bold`} />
                  <td className={`${stickyDateTotalsClass} ${cellPadClass} font-bold text-gray-900`}>
                    Total paid
                  </td>
                  <td className={`${JE_COL_CLASS} ${STICKY_TOTALS_CLASS} ${cellPadClass}`} />
                  {MEMBERS.map((m) => (
                    <td
                      key={m}
                      className={`${numColClass} ${STICKY_TOTALS_CLASS} ${cellPadClass} text-right tabular-nums font-bold ${
                        cumulativeTotals.byMember[m] < 0 ? 'text-red-600' : 'text-gray-900'
                      }`}
                    >
                      {fmt.format(cumulativeTotals.byMember[m])}
                    </td>
                  ))}
                  <td
                    className={`${numColClass} ${STICKY_TOTALS_CLASS} ${cellPadClass} text-right tabular-nums font-bold text-gray-900 ${TOTAL_COL_BORDER}`}
                  >
                    {fmt.format(cumulativeTotals.total)}
                  </td>
                  <td
                    className={`${numColClass} ${STICKY_TOTALS_CLASS} ${cellPadClass} text-right tabular-nums font-bold text-gray-900`}
                  >
                    {fmt.format(cumulativeTotals.cashTotal)}
                  </td>
                  <td className={`${STICKY_TOTALS_CLASS} ${noteColClass} ${cellPadClass} font-bold`} />
                </tr>
                {rows.map((row) => {
                  const rowKey = String(row.id)
                  const isExpanded = expanded.has(rowKey)
                  const isNoteExpanded = noteExpanded.has(rowKey)
                  const tiers = nonEmptyTiers(row)
                  const canExpand = tiers.length > 0
                  const hasNote = Boolean(row.note?.trim())
                  return (
                    <Fragment key={row.id}>
                      {isExpanded && canExpand
                        ? tiers.map((tierKey) => {
                            const tier = row[tierKey]
                            const tierSum = tierTotal(tier)
                            return (
                              <tr
                                key={`${row.id}-${tierKey}`}
                                className="text-xs text-gray-600 border-t border-gray-100"
                              >
                                <td className={`${STICKY_NUM_CHILD_CLASS} ${cellPadClass}`} />
                                <td
                                  className={`${stickyDateChildClass} ${cellPadClass}`}
                                  style={{ paddingLeft: maxMd ? '1.75rem' : '2.5rem' }}
                                >
                                  {TIER_LABELS[tierKey]}
                                </td>
                                <td className={`${JE_COL_CLASS} ${cellPadClass}`} />
                                {MEMBERS.map((m) => {
                                  const amount = Number(tier[m])
                                  const cellKey = verifiedCellKey(row.id, tierKey, m)
                                  return (
                                    <VerifiableTierAmountCell
                                      key={m}
                                      cellKey={cellKey}
                                      amount={amount}
                                      isVerified={verifiedCells.has(cellKey)}
                                      onToggle={toggleVerified}
                                      colClass={numColClass}
                                      touchFriendly={maxMd}
                                    />
                                  )
                                })}
                                <VerifiableTierAmountCell
                                  cellKey={verifiedCellKey(row.id, tierKey, 'total')}
                                  amount={tierSum}
                                  isVerified={verifiedCells.has(
                                    verifiedCellKey(row.id, tierKey, 'total'),
                                  )}
                                  onToggle={toggleVerified}
                                  colClass={`${numColClass} ${TOTAL_COL_BORDER}`}
                                  touchFriendly={maxMd}
                                />
                                <td className={`${numColClass} ${cellPadClass}`} />
                                <td className={`${noteColClass} ${cellPadClass}`} />
                              </tr>
                            )
                          })
                        : null}
                      <tr
                        className={`group ${isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                      >
                        <td
                          className={`${
                            isExpanded
                              ? 'sticky left-0 z-20 w-12 min-w-[3rem] max-w-[3rem] bg-gray-50 border-r border-gray-200 font-medium'
                              : STICKY_NUM_CLASS
                          } ${cellPadClass} text-gray-700`}
                        >
                          {row.num}
                        </td>
                        <td
                          className={`${
                            isExpanded ? STICKY_DATE_EXPANDED_CLASS : stickyDateClass
                          } ${cellPadClass} text-gray-700`}
                        >
                          {canExpand ? (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(rowKey)}
                              className="inline-flex items-center gap-1.5 text-left hover:text-brand-700 max-md:min-h-11 max-md:py-1"
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              <span className="text-gray-400 w-3">
                                {isExpanded ? '▾' : '▸'}
                              </span>
                              {row.date}
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 pl-[1.125rem]">
                              {row.date}
                            </span>
                          )}
                        </td>
                        <td className={`${JE_COL_CLASS} ${cellPadClass} ${isExpanded ? 'bg-gray-50' : ''}`}>
                          {canEdit || row.je_status !== 'none' ? (
                            <DistributionJeRowButton
                              status={row.je_status}
                              loading={jeLoadingNum === row.num}
                              onClick={() => void handleJeRowClick(row)}
                            />
                          ) : null}
                        </td>
                        {MEMBERS.map((m) => {
                          const amount = Number(row.by_member[m])
                          const withheld = memberWithheldTotal(row, m)
                          return (
                            <td
                              key={m}
                              className={`${numColClass} ${cellPadClass} text-right tabular-nums ${
                                isExpanded ? 'bg-gray-50 font-medium' : ''
                              } ${amount < 0 ? 'text-red-600' : 'text-gray-900'}`}
                            >
                              <span className="inline-flex flex-wrap items-center justify-end gap-1">
                                {fmt.format(amount)}
                                {m === 'SM' && withheld > 0.005 && (
                                  <HoverHelp label="gross" aria-label="Gross including withheld">
                                    <p>gross incl. {fmt.format(withheld)} withheld</p>
                                  </HoverHelp>
                                )}
                              </span>
                            </td>
                          )
                        })}
                        <td
                          className={`${numColClass} ${cellPadClass} text-right tabular-nums font-medium ${TOTAL_COL_BORDER} ${
                            isExpanded ? 'bg-gray-50' : ''
                          }`}
                        >
                          <div className="flex flex-col items-end gap-0.5">
                            {fmt.format(row.total)}
                            {Number(row.withheld_total ?? 0) > 0.005 && (
                              <span className="text-xs text-gray-500 font-normal">
                                withheld {fmt.format(Number(row.withheld_total))}
                              </span>
                            )}
                            {rowHasCatchUp(row) && (
                              <span className="text-xs text-gray-500 font-normal">catch-up</span>
                            )}
                          </div>
                        </td>
                        <td
                          className={`${numColClass} ${cellPadClass} text-right tabular-nums ${
                            isExpanded ? 'bg-gray-50 font-medium' : ''
                          }`}
                        >
                          {fmt.format(Number(row.cash_total ?? row.total))}
                        </td>
                        <td
                          className={`${noteColClass} ${cellPadClass} align-top ${
                            isExpanded ? 'bg-gray-50' : ''
                          }`}
                        >
                          {maxMd ? (
                            <button
                              type="button"
                              onClick={() => toggleNoteExpanded(rowKey)}
                              className={`inline-flex min-h-11 min-w-11 items-center justify-center rounded-md ${
                                hasNote || isNoteExpanded
                                  ? 'text-brand-600 bg-brand-50'
                                  : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                              }`}
                              aria-expanded={isNoteExpanded}
                              aria-label={
                                hasNote ? 'View or edit note' : 'Add note'
                              }
                            >
                              <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                          ) : (
                            <NoteCell row={row} onSaved={handleNoteSaved} />
                          )}
                        </td>
                      </tr>
                      {maxMd && isNoteExpanded ? (
                        <tr className="bg-gray-50 border-t border-gray-100">
                          <td colSpan={tableColCount} className="px-4 py-3">
                            <p className="mb-2 text-xs font-medium text-gray-500">Note</p>
                            <NoteCell row={row} onSaved={handleNoteSaved} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default function History() {
  const mode = useDistributionModeStore((s) => s.mode)
  return (
    <div className="flex flex-col items-start">
      <DistributionModeToggle className="mb-6" />
      <div className="w-full">
        {mode === 'income' ? <AllocationHistory /> : <HistoryCash />}
      </div>
    </div>
  )
}
